import React from 'react';
import { Link } from 'react-router-dom';
import { Home, Search, ArrowLeft, Brain, Target, Code, Cloud, Bot, Sparkles } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useBanner } from '../contexts/BannerContext';

export const NotFoundPage: React.FC = () => {
  useSEO('404' as any);
  const { isBannerVisible, bannerHeight } = useBanner();

  const popularPages = [
    { icon: <Target className="w-5 h-5" />, title: "Services", href: "/services", description: "AI & Blockchain Solutions" },
    { icon: <Brain className="w-5 h-5" />, title: "About", href: "/about", description: "Learn About Our Company" },
    { icon: <Cloud className="w-5 h-5" />, title: "Contact", href: "/contact", description: "Get in Touch" }
  ];

  const featuredServices = [
    { icon: <Bot className="w-4 h-4" />, title: "Cognitive Agents", href: "/services/cognitive-agents" },
    { icon: <Sparkles className="w-4 h-4" />, title: "Cognitive Generate", href: "/services/cognitive-generate" },
    { icon: <Brain className="w-4 h-4" />, title: "Cognitive Intelligence", href: "/services/cognitive-intelligence" }
  ];

  return (
    <main 
      className="pb-20"
      style={{ 
        paddingTop: isBannerVisible ? `${bannerHeight + 100}px` : '100px'
      }}
    >
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="mb-8">
              <div className="w-32 h-32 bg-gradient-to-r from-blue-600/20 to-blue-700/20 rounded-full mx-auto mb-8 flex items-center justify-center">
                <Search className="w-16 h-16 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            
            <h1 className="text-6xl md:text-8xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-blue-700 bg-clip-text text-transparent">
              404
            </h1>
            
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-6">
              Page Not Found
            </h2>
            
            <p className="text-xl text-gray-700 dark:text-gray-300 max-w-2xl mx-auto leading-relaxed mb-8">
              The page you're looking for doesn't exist or has been moved. 
              Let's help you find what you need from our AI and blockchain solutions.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
              >
                <Home className="w-5 h-5 mr-2" />
                Go Home
              </Link>
              
              <button
                onClick={() => window.history.back()}
                className="inline-flex items-center px-8 py-4 border-2 border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 hover:border-gray-400 dark:hover:border-white/50 transition-all duration-300"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Go Back
              </button>
            </div>
          </div>

          {/* Popular Pages */}
          <section className="mb-16">
            <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-8 text-center">
              Popular Pages
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {popularPages.map((page, index) => (
                <Link
                  key={index}
                  to={page.href}
                  className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-gray-200 dark:border-white/10 text-center hover:bg-gray-50 dark:hover:bg-white/10 hover:border-blue-600 dark:hover:border-blue-400 hover:shadow-lg transform hover:scale-105 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl mx-auto mb-4 flex items-center justify-center text-white">
                    {page.icon}
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {page.title}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {page.description}
                  </p>
                </Link>
              ))}
            </div>
          </section>

          {/* Featured Services */}
          <section className="mb-16">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-3xl border border-blue-200 dark:border-blue-500/30 p-8">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 text-center">
                Featured AI Services
              </h3>
              
              <div className="grid md:grid-cols-3 gap-6 mb-6">
                {featuredServices.map((service, index) => (
                  <Link
                    key={index}
                    to={service.href}
                    className="bg-white/60 dark:bg-white/10 rounded-xl p-4 text-center hover:bg-white/80 dark:hover:bg-white/20 transition-all duration-300"
                  >
                    <div className="text-blue-600 dark:text-blue-400 mb-2 flex justify-center">
                      {service.icon}
                    </div>
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {service.title}
                    </h4>
                  </Link>
                ))}
              </div>
              
              <div className="text-center">
                <Link
                  to="/services"
                  className="inline-flex items-center text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                >
                  View All Services →
                </Link>
              </div>
            </div>
          </section>

          {/* Help Section */}
          <section>
            <div className="bg-white/80 dark:bg-white/5 backdrop-blur-sm rounded-3xl border border-gray-200 dark:border-white/10 p-8 text-center">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Need Help Finding Something?
              </h3>
              
              <p className="text-lg text-gray-700 dark:text-gray-300 mb-8 max-w-2xl mx-auto">
                Our team is here to help you navigate our comprehensive AI and blockchain solutions. 
                Contact us for personalized assistance.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contact"
                  className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300"
                >
                  Contact Support
                </Link>
                
                <button
                  onClick={() =>
                    window.open(
                      "https://outlook.office.com/book/CognitiveCraftHub2@cognitivecrafthub.com/?ismsaljsauthenabled",
                      "_blank"
                    )
                  }
                  className="px-6 py-3 border border-gray-300 dark:border-white/30 text-gray-700 dark:text-white font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-white/10 transition-all duration-300"
                >
                  Schedule Consultation
                </button>
              </div>
            </div>
          </section>
        </div>
      </section>
    </main>
  );
};